import {Trans} from '@lingui/macro';
import React, {FC, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {IntegrationFileAttachment} from '../../../../helpers/integrationHelpers';
import {CalendarProviderTypesEnum} from '../../../../models/api/calendarModel';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {AttachmentFileUploader} from '../../../common/attachments/attachmentsTypes';
import {useIntegrationAttachments} from '../../../inboxes/composer/toolbar/buttons/attachmentsHooks/attachmentsHook';
import {renderAttachmentButton} from './calendarAttachmentButton';
import {CalendarAttachmentsDropdownCoordinator} from './calendarAttachmentsDropdownCoordinator';

/*
 * Props.
 */

export interface CalendarAttachmentsButtonCoordinatorPublicProps {
  onIntegrationAttachLinks: HandlerOf<ReadonlyArray<IntegrationFileAttachment>>;
  onFileUpload: AttachmentFileUploader;
  provider: CalendarProviderTypesEnum;
}

interface CalendarAttachmentsButtonCoordinatorProps extends CalendarAttachmentsButtonCoordinatorPublicProps {
  integrations: ReadonlyArray<IntegrationModel>;
}

/*
 * Style.
 */

const StyledFileLabel = styled.label`
  display: none;
`;

/*
 * Component.
 */

export const CalendarAttachmentsButtonCoordinator: FC<CalendarAttachmentsButtonCoordinatorProps> = (
  props,
) => {
  const {onIntegrationAttachLinks, onFileUpload} = props;

  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Event handlers that open up the computer file selector.
  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      // For each file in the input, add it.
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        fileList.forEach((f) => onFileUpload(f));
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileUpload, setInputKey],
  );

  const onAttachFromComputerClick = useCallback(() => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  }, []);

  const {showIntegrationAttachmentsPicker} = useIntegrationAttachments(onIntegrationAttachLinks);
  const onIntegrationAttachClick = showIntegrationAttachmentsPicker;

  /*
   * Render.
   */

  const renderInputFilePicker = () => (
    <StyledFileLabel>
      <input ref={fileInputRef} key={inputKey} type="file" onChange={onFileInputChange} multiple />
    </StyledFileLabel>
  );

  return (
    <>
      {renderInputFilePicker()}
      {renderAttachmentsButtonCoordinator(props, onAttachFromComputerClick, onIntegrationAttachClick)}
    </>
  );
};

// The attachments button differs depending on the provider and which integrations are available.
function renderAttachmentsButtonCoordinator(
  props: CalendarAttachmentsButtonCoordinatorProps,
  onAttachFromComputerClick: Handler,
  onIntegrationAttachClick: HandlerOf<IntegrationModel>,
) {
  const {provider, integrations} = props;
  switch (provider) {
    case CalendarProviderTypesEnum.OFFICE365:
      // The office graph API currently doesn't support remote attachments (`referenceAttachment`). When they do, we can add integrations.
      return renderAttachFromComputerButton(onAttachFromComputerClick);
    case CalendarProviderTypesEnum.GOOGLE:
      if (integrations.length === 0) {
        // When no integrations are available, show a disabled dropdown coordinator.
        return (
          <CalendarAttachmentsDropdownCoordinator
            integrations={integrations}
            onIntegrationAttachClick={onIntegrationAttachClick}
            isDisabled
          />
        );
      }
      // When there's just one integration, directly show it as a button.
      if (integrations.length === 1) {
        return renderSingleIntegrationButton(integrations[0], onIntegrationAttachClick);
      }
      // When there are multiple integrations, show the dropdown coordinator.
      return (
        <CalendarAttachmentsDropdownCoordinator
          integrations={integrations}
          onIntegrationAttachClick={onIntegrationAttachClick}
        />
      );
    default:
      reportToBugsnag(new Error('Rendering attachments button for event with unrecognized provider type.'));
      return null;
  }
}

function renderAttachFromComputerButton(onAttachFromComputerClick: Handler) {
  return renderAttachmentButton({
    onClick: onAttachFromComputerClick,
    buttonText: <Trans id="calendar-attachments-button-attach-from-computer">Attach from computer</Trans>,
    shouldShowChevron: false,
  });
}

function renderSingleIntegrationButton(
  integration: IntegrationModel,
  onIntegrationAttachClick: HandlerOf<IntegrationModel>,
) {
  const integrationButtonText = (
    <Trans id="calendar-attachments-button-attach-with-integration">Attach with {integration.name}</Trans>
  );
  return renderAttachmentButton({
    onClick: () => onIntegrationAttachClick(integration),
    buttonText: integrationButtonText,
    shouldShowChevron: false,
  });
}
